import React, { useEffect, useState, useCallback } from 'react';
import { List_gig_deletion_logs } from '../../Utils/Api';

const REASON_LABELS = {
  1:  'No Skillsters found',
  2:  'Too few proposals',
  3:  'Background check failed',
  4:  'Budget mismatch',
  5:  'Not worth the cost',
  6:  'Scope changed',
  7:  'DIY',
  8:  'Hired externally',
  9:  'Listing mistake',
  10: 'Off-platform payment',
  11: 'Other',
};

const REASON_BADGE_STYLE = {
  1:  { background: '#dbeafe', color: '#1e40af' },
  2:  { background: '#e0e7ff', color: '#3730a3' },
  3:  { background: '#ede9fe', color: '#6d28d9' },
  4:  { background: '#ffedd5', color: '#c2410c' },
  5:  { background: '#fee2e2', color: '#b91c1c' },
  6:  { background: '#fef9c3', color: '#92400e' },
  7:  { background: '#dcfce7', color: '#15803d' },
  8:  { background: '#ccfbf1', color: '#0f766e' },
  9:  { background: '#fce7f3', color: '#be185d' },
  10: { background: '#fef3c7', color: '#92400e' },
  11: { background: '#f3f4f6', color: '#374151' },
};

const GigDeletionLogs = () => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1, limit: 20 });
  const [reasonBreakdown, setReasonBreakdown] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [page, setPage] = useState(1);

  const fetchLogs = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const params = {
        page: overrides.page ?? page,
        limit: 20,
        ...(overrides.search !== undefined ? (overrides.search ? { search: overrides.search } : {}) : (search ? { search } : {})),
        ...(overrides.reasonCode !== undefined ? (overrides.reasonCode ? { reasonCode: overrides.reasonCode } : {}) : (reasonFilter ? { reasonCode: reasonFilter } : {})),
      };
      const res = await List_gig_deletion_logs(params);
      if (res?.data?.success) {
        setLogs(res.data.data || []);
        setPagination(res.data.pagination || {});
        if (res.data.reasonBreakdown) setReasonBreakdown(res.data.reasonBreakdown);
      }
    } catch (err) {
      console.error('Failed to fetch deletion logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, reasonFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs({ page: 1 });
  };

  const changePage = (newPage) => {
    setPage(newPage);
    fetchLogs({ page: newPage });
  };

  const topReasons = [...reasonBreakdown].sort((a, b) => b.count - a.count).slice(0, 5);

  return (
    <div className="container-fluid">
      {/* Page Title */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="mb-0" style={{ fontWeight: 700 }}>
            🗑️ Gig Deletion Logs
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            Admin view of all deleted gigs and reported reasons
          </p>
        </div>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => fetchLogs()}
          disabled={loading}
        >
          {loading ? '⟳ Loading…' : '⟳ Refresh'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-auto">
          <div className="card border-0 shadow-sm" style={{ minWidth: 130 }}>
            <div className="card-body py-3 px-4">
              <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>Total Deletions</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#dc2626' }}>{pagination.total ?? '—'}</div>
            </div>
          </div>
        </div>
        {topReasons.map(r => (
          <div className="col-auto" key={r._id}>
            <button
              onClick={() => {
                const next = reasonFilter === String(r._id) ? '' : String(r._id);
                setReasonFilter(next);
                setPage(1);
                fetchLogs({ page: 1, reasonCode: next || undefined });
              }}
              className="card border-0 shadow-sm text-start w-100"
              style={{
                minWidth: 130,
                cursor: 'pointer',
                border: reasonFilter === String(r._id) ? '2px solid #dc2626' : '1px solid #e5e7eb',
                background: reasonFilter === String(r._id) ? '#fff5f5' : '#fff',
                transition: 'all 0.15s'
              }}
            >
              <div className="card-body py-3 px-4">
                <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
                  {REASON_LABELS[r._id] || `Reason ${r._id}`}
                </div>
                <div style={{ fontSize: 26, fontWeight: 800, color: reasonFilter === String(r._id) ? '#dc2626' : '#111827' }}>
                  {r.count}
                </div>
              </div>
            </button>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <form onSubmit={handleSearch} className="d-flex flex-wrap gap-2 align-items-center">
            <div className="input-group" style={{ maxWidth: 360 }}>
              <span className="input-group-text bg-white"><i className="bx bx-search" /></span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by gig title or user email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select
              className="form-select"
              style={{ maxWidth: 200 }}
              value={reasonFilter}
              onChange={e => {
                setReasonFilter(e.target.value);
                setPage(1);
                fetchLogs({ page: 1, reasonCode: e.target.value || undefined });
              }}
            >
              <option value="">All Reasons</option>
              {Object.entries(REASON_LABELS).map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
            <button type="submit" className="btn btn-danger btn-sm px-4">
              Search
            </button>
            {(search || reasonFilter) && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setSearch('');
                  setReasonFilter('');
                  setPage(1);
                  fetchLogs({ page: 1, search: '', reasonCode: '' });
                }}
              >
                Clear
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5 text-muted">
              <div className="spinner-border spinner-border-sm me-2" role="status" />
              Loading logs…
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <div style={{ fontSize: 32 }}>🗑️</div>
              <p className="mt-2 mb-0">No deletion logs found.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle">
                <thead style={{ background: '#f9fafb', fontSize: 12, textTransform: 'uppercase', color: '#6b7280' }}>
                  <tr>
                    <th className="px-4 py-3">User</th>
                    <th className="py-3">Gig Title</th>
                    <th className="py-3">Reason</th>
                    <th className="py-3">Custom Details</th>
                    <th className="py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={log._id || i}>
                      {/* User */}
                      <td className="px-4 py-3">
                        <div className="d-flex align-items-center gap-2">
                          {log.userId?.profilePicture ? (
                            <img
                              src={log.userId.profilePicture}
                              alt={log.userId.name}
                              style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '1px solid #e5e7eb' }}
                              onError={e => e.target.style.display = 'none'}
                            />
                          ) : (
                            <div style={{
                              width: 34, height: 34, borderRadius: '50%',
                              background: 'linear-gradient(135deg,#64748b,#334155)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0
                            }}>
                              {log.userId?.name?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>{log.userId?.name || 'Unknown'}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af' }}>{log.userId?.email || '—'}</div>
                          </div>
                        </div>
                      </td>

                      {/* Gig Title */}
                      <td className="py-3" style={{ maxWidth: 200 }}>
                        <span
                          title={log.gigTitle}
                          style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13 }}
                        >
                          {log.gigTitle || <span className="text-muted fst-italic">Untitled</span>}
                        </span>
                      </td>

                      {/* Reason Badge */}
                      <td className="py-3">
                        <span style={{
                          ...(REASON_BADGE_STYLE[log.reasonCode] || { background: '#f3f4f6', color: '#374151' }),
                          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap'
                        }}>
                          {REASON_LABELS[log.reasonCode] || `Reason ${log.reasonCode}`}
                        </span>
                      </td>

                      {/* Custom text */}
                      <td className="py-3" style={{ maxWidth: 200 }}>
                        <span
                          title={log.reasonText}
                          style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}
                        >
                          {log.reasonText || '—'}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="py-3" style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                        {log.createdAt
                          ? new Date(log.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="d-flex align-items-center justify-content-between px-4 py-3 border-top">
              <span className="text-muted" style={{ fontSize: 12 }}>
                Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of <strong>{pagination.total}</strong>
              </span>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => changePage(page - 1)}>‹</button>
                  </li>
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - page) <= 1)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, i) =>
                      p === '…' ? (
                        <li key={`e-${i}`} className="page-item disabled"><span className="page-link">…</span></li>
                      ) : (
                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => changePage(p)}>{p}</button>
                        </li>
                      )
                    )}
                  <li className={`page-item ${page >= pagination.totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => changePage(page + 1)}>›</button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GigDeletionLogs;
